/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api } from "../../lib/api";
import { clearNavMenuCache } from "../../lib/navMenus";
import { toast } from "sonner";
import {
  ArrowUp, ArrowDown, Plus, Trash, FloppyDisk, ArrowClockwise, Info,
  Eye, EyeSlash, House, SignIn, Article, Scales,
} from "@phosphor-icons/react";

const TABS = [
  { key: "main",   label: "Top / Main Menu",   Icon: House,   blurb: "Flat links displayed in the top navigation (after the Solutions / Resources dropdowns)." },
  { key: "signin", label: "Sign-in Menu",       Icon: SignIn,  blurb: "Items shown in the “Sign in” dropdown on the marketing top-right." },
  { key: "footer", label: "Footer Menu",        Icon: Article, blurb: "Links displayed in the main 'Explore' column of the marketing footer." },
  { key: "legal",  label: "Privacy / Terms",    Icon: Scales,  blurb: "Links displayed in the 'Legal' column of the marketing footer." },
];

export default function AdminNavMenus() {
  const [tab, setTab] = useState("main");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async (k = tab) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/nav-menus/${k}`);
      setItems(r.data.items || []);
      setSavedAt(r.data.updated_at || null);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load menu");
    }
    setLoading(false);
  };
  useEffect(() => { load(tab); }, [tab]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/nav-menus/${tab}`, { items });
      clearNavMenuCache();
      toast.success("Menu saved");
      load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const reset = async () => {
    if (!window.confirm("Revert this menu to the platform defaults? Your customisations will be lost.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/nav-menus/${tab}/reset`);
      clearNavMenuCache();
      toast.success("Menu reset to defaults");
      load(tab);
    } catch (e) { toast.error("Reset failed"); }
    setBusy(false);
  };

  const upd = (i, k, v) => {
    const next = items.slice();
    next[i] = { ...next[i], [k]: v };
    setItems(next); setDirty(true);
  };
  const swap = (i, j) => {
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next); setDirty(true);
  };
  const remove = (i) => {
    if (!window.confirm(`Remove "${items[i].label}" from this menu?`)) return;
    setItems(items.filter((_, k) => k !== i)); setDirty(true);
  };
  const add = () => {
    setItems([...items, { path: "/", label: "New item", enabled: true }]); setDirty(true);
  };

  const active = TABS.find(t => t.key === tab);

  return (
    <AppShell title="Menu Navigation" subtitle="Content Management · Edit menus shown to visitors">
      <div className="max-w-4xl space-y-4">
        {/* Tab strip */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-1.5 flex gap-1 overflow-x-auto" data-testid="nav-menu-tabs">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
                     data-testid={`nav-tab-${t.key}`}
                     className={`h-9 px-3 rounded text-xs font-medium whitespace-nowrap inline-flex items-center gap-1.5 ${
                       tab === t.key ? "bg-[#2D5A27] text-white" : "text-[#5C5A55] hover:bg-[#F7F6F2]"
                     }`}>
              <t.Icon size={11} weight={tab === t.key ? "fill" : "regular"}/> {t.label}
            </button>
          ))}
        </div>

        {/* Header card */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded bg-[#F4F8F2] grid place-items-center text-[#2D5A27]">
              <active.Icon size={16} weight="duotone"/>
            </div>
            <div className="flex-1">
              <div className="font-heading text-lg">{active.label}</div>
              <p className="text-xs text-[#5C5A55] mt-0.5">{active.blurb}</p>
            </div>
            {savedAt && (
              <div className="text-[11px] text-[#8A8780] hidden md:block">
                Saved · {new Date(savedAt).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {/* Item list */}
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden" data-testid="nav-menu-list">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#8A8780]">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center">
              <Info size={20} color="#8A8780"/>
              <div className="mt-3 text-sm text-[#5C5A55]">No items yet. Add one below.</div>
            </div>
          ) : (
            <ul>
              {items.map((it, i) => (
                <li key={i} data-testid={`nav-item-${i}`}
                     className="border-b border-[#F0ECE3] last:border-b-0 px-4 py-3 flex items-center gap-3">
                  {/* Reorder */}
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => swap(i, i - 1)} disabled={i === 0}
                             data-testid={`nav-item-up-${i}`}
                             className="w-6 h-6 rounded hover:bg-[#F7F6F2] disabled:opacity-30 disabled:cursor-not-allowed">
                      <ArrowUp size={11} className="mx-auto"/>
                    </button>
                    <button onClick={() => swap(i, i + 1)} disabled={i === items.length - 1}
                             data-testid={`nav-item-down-${i}`}
                             className="w-6 h-6 rounded hover:bg-[#F7F6F2] disabled:opacity-30 disabled:cursor-not-allowed">
                      <ArrowDown size={11} className="mx-auto"/>
                    </button>
                  </div>

                  {/* Label + Path */}
                  <div className="grid grid-cols-2 gap-2 flex-1 min-w-0">
                    <input value={it.label} onChange={e => upd(i, "label", e.target.value)}
                            data-testid={`nav-item-label-${i}`}
                            placeholder="Label"
                            className="h-9 px-2 text-sm border border-[#E5E2D9] rounded"/>
                    <input value={it.path} onChange={e => upd(i, "path", e.target.value)}
                            data-testid={`nav-item-path-${i}`}
                            placeholder="/path"
                            className="h-9 px-2 text-sm font-mono border border-[#E5E2D9] rounded"/>
                  </div>

                  {/* Toggle */}
                  <button onClick={() => upd(i, "enabled", !it.enabled)}
                           data-testid={`nav-item-toggle-${i}`}
                           title={it.enabled ? "Visible — click to hide" : "Hidden — click to show"}
                           className={`h-9 w-9 rounded inline-flex items-center justify-center transition-colors ${
                             it.enabled ? "bg-[#2D5A27] text-white" : "bg-[#F0ECE3] text-[#8A8780]"
                           }`}>
                    {it.enabled ? <Eye size={13}/> : <EyeSlash size={13}/>}
                  </button>

                  <button onClick={() => remove(i)} data-testid={`nav-item-remove-${i}`}
                           className="h-9 w-9 rounded text-[#7A2E18] hover:bg-[#FDECE6] inline-flex items-center justify-center">
                    <Trash size={13}/>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer toolbar */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-4 flex items-center gap-3 flex-wrap">
          <button onClick={add} data-testid="nav-add-item"
                   className="h-9 px-3 rounded text-xs border border-dashed border-[#2D5A27] text-[#2D5A27] inline-flex items-center gap-1.5">
            <Plus size={11}/> Add item
          </button>
          <button onClick={reset} disabled={busy}
                   className="h-9 px-3 rounded text-xs border border-[#E5E2D9] hover:border-[#2D5A27] text-[#5C5A55] inline-flex items-center gap-1.5">
            <ArrowClockwise size={11}/> Reset to defaults
          </button>
          <div className="ml-auto flex items-center gap-2">
            {dirty && <span className="text-[11px] text-[#C25D38]">Unsaved changes</span>}
            <button onClick={save} disabled={!dirty || busy} data-testid="nav-save"
                     className="h-9 px-4 rounded text-xs font-medium bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white inline-flex items-center gap-1.5">
              <FloppyDisk size={11}/> {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        <div className="text-[11px] text-[#8A8780] inline-flex items-center gap-1.5">
          <Info size={11}/> Changes go live immediately after save. Eye toggle controls visibility; path/label edits go straight into the menu.
        </div>
      </div>
    </AppShell>
  );
}
